import { LucideInfo } from 'lucide-react'
import { createElement } from 'react'
import { toast as sonnerToast, useSonner } from 'sonner'
import type { ToastProps, ToastVariant } from '~/components/ui/toast'

const DEFAULT_DURATION = 5_000
const toastPropsById = new Map<string | number, ToastProps>()
let toastCount = 0

function createToastId() {
  toastCount = (toastCount + 1) % Number.MAX_SAFE_INTEGER
  return toastCount.toString()
}

function getToastMessage({ title, description }: ToastProps) {
  return title ?? description ?? ''
}

function getToastOptions({
  action,
  description,
  title,
  ...options
}: ToastProps) {
  return {
    ...options,
    action: action
      ? {
          label: action.props.children,
          onClick: action.props.onClick,
        }
      : undefined,
    description: title ? description : undefined,
  }
}

function showToast(toastProps: ToastProps, id: string | number) {
  const message = getToastMessage(toastProps)
  const options = {
    ...getToastOptions(toastProps),
    duration: toastProps.duration ?? DEFAULT_DURATION,
    id,
    onAutoClose: () => toastPropsById.delete(id),
    onDismiss: () => toastPropsById.delete(id),
  }

  switch (toastProps.variant as ToastVariant) {
    case 'success':
      return sonnerToast.success(message, options)
    case 'destructive':
      return sonnerToast.error(message, options)
    case 'warning':
      return sonnerToast.warning(message, options)
    default:
      return sonnerToast(message, {
        ...options,
        icon: createElement(LucideInfo, { size: 17 }),
      })
  }
}

function toast(toastProps: ToastProps) {
  const id = createToastId()
  toastPropsById.set(id, toastProps)
  showToast(toastProps, id)

  return {
    dismiss: () => {
      toastPropsById.delete(id)
      sonnerToast.dismiss(id)
    },
    id,
    update: (nextToastProps: ToastProps) => {
      const updatedToastProps = {
        ...toastPropsById.get(id),
        ...nextToastProps,
      }

      toastPropsById.set(id, updatedToastProps)
      return showToast(updatedToastProps, id)
    },
  }
}

function dismiss(toastId?: string | number) {
  if (toastId === undefined) {
    toastPropsById.clear()
  } else {
    toastPropsById.delete(toastId)
  }

  return sonnerToast.dismiss(toastId)
}

function useToast() {
  const { toasts } = useSonner()

  return {
    dismiss,
    toast,
    toasts,
  }
}

export { toast, useToast }
